/**
 * URL helpers for the split-hostname deployment.
 *
 * - `condorsecurity.co`        → public landing ("LANDING")
 * - `app.condorsecurity.co`    → authenticated SPA ("APP")
 *
 * The same Vite build is served from both hostnames (via the same Cloudflare
 * tunnel → localhost:3000 upstream). Code decides what to render/redirect
 * based on `window.location.hostname`.
 */

const DEFAULT_APP_URL = 'https://app.condorsecurity.co';
const DEFAULT_LANDING_URL = 'https://condorsecurity.co';

/** Absolute URL of the authenticated app (env-overridable via VITE_APP_URL). */
export const APP_URL: string = import.meta.env.VITE_APP_URL || DEFAULT_APP_URL;

/** Absolute URL of the marketing landing (env-overridable via VITE_LANDING_URL). */
export const LANDING_URL: string = import.meta.env.VITE_LANDING_URL || DEFAULT_LANDING_URL;

/**
 * Build an absolute URL on the app subdomain, preserving the current origin
 * when it already matches (prevents unnecessary full-page reloads) and when
 * running in dev (APP_URL/LANDING_URL then both point at localhost).
 */
export function appUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  try {
    const target = new URL(APP_URL);
    if (typeof window !== 'undefined' && window.location.origin === target.origin) {
      return normalized;
    }
    return `${target.origin}${normalized}`;
  } catch {
    return `${APP_URL.replace(/\/$/, '')}${normalized}`;
  }
}

/**
 * Build an absolute URL on the landing hostname. Same origin-preserving
 * behaviour as `appUrl`.
 */
export function landingUrl(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  try {
    const target = new URL(LANDING_URL);
    if (typeof window !== 'undefined' && window.location.origin === target.origin) {
      return normalized;
    }
    return `${target.origin}${normalized}`;
  } catch {
    return `${LANDING_URL.replace(/\/$/, '')}${normalized}`;
  }
}

/** True when the current page is hosted on the authenticated app origin. */
export function isOnAppHost(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.location.origin === new URL(APP_URL).origin;
  } catch {
    return false;
  }
}

/** True when the current page is hosted on the public landing origin. */
export function isOnLandingHost(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.location.origin === new URL(LANDING_URL).origin;
  } catch {
    return false;
  }
}

/**
 * True when the SPA is served from a hostname that is neither the app nor
 * the landing origin — i.e. a customer's custom trust-center domain
 * (CNAME → trust.condorsecurity.co). localhost/127.0.0.1 always count as
 * normal dev, never as a custom host.
 */
export function isCustomTrustHost(): boolean {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return false;
  return !isOnAppHost() && !isOnLandingHost();
}

/**
 * Navigate to a path on the authenticated app.
 *
 * If we are already on the app origin, use the supplied router `navigate`
 * (SPA transition). Otherwise do a full-page load to cross the hostname
 * boundary (cookies/session + SPA state must re-hydrate on the right origin).
 */
export function goToApp(path: string, navigate?: (p: string) => void): void {
  const target = appUrl(path);
  const isRelative = target.startsWith('/');
  if (isRelative && navigate) {
    navigate(target);
    return;
  }
  if (typeof window !== 'undefined') {
    window.location.href = target;
  }
}

/**
 * Destino post-login: el `?next=` de la URL, si es seguro; si no, `/dashboard`.
 *
 * Quien manda a `/login?next=<destino>` espera volver ahi. El caso que lo hizo
 * evidente es el OAuth del MCP: `/oauth/authorize` redirige al portal con
 * `?next=<authorize>`, y al ignorarlo el usuario terminaba en el dashboard con
 * la autorizacion abandonada — el cliente MCP veia "Authentication failed" sin
 * ninguna causa visible.
 *
 * Un `next` sin validar es un open redirect, asi que sólo se aceptan destinos
 * del MISMO origen. Se admite tanto una ruta relativa (`/oauth/authorize?...`)
 * como una URL absoluta cuyo origen coincida con el actual; cualquier otra cosa
 * —`//evil.com`, `https://evil.com`, `javascript:`— cae al fallback.
 */
export function safeNextPath(next: string | null | undefined, fallback = '/dashboard'): string {
  if (!next) return fallback;

  // `//evil.com` es protocol-relative: el navegador lo trata como absoluto.
  if (next.startsWith('//')) return fallback;

  if (next.startsWith('/')) return next;

  // Absoluta: sólo se acepta si es exactamente el mismo origen.
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : undefined;
    if (!base) return fallback;
    const url = new URL(next, base);
    if (url.origin !== base) return fallback;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return fallback;
  }
}
